import { jsx, type JsxElementProps, onConnect, setVisible } from '@amazon/vinyl-tsx'
import { asData, data, type MaybeObservableValue } from '@amazon/vinyl-observable'
import type { Maybe } from 'graphql/jsutils/Maybe'
import { omit } from '@/util/objectUtil'
import { doWhenIdle } from '@/util/idleQueue'
import { createDisposer, DomEventHost, nextEventAsPromise, noop } from '@amazon/vinyl-util'
import { isIntersectingProvider } from '@/util/domUtil'

export interface ImageModel {
    src?: Maybe<string>
    srcset?: Maybe<string>
    width?: Maybe<number>
    height?: Maybe<number>
}

export type ImageProps = JsxElementProps<'div'> & {
    image: MaybeObservableValue<Maybe<ImageModel>>

    /**
     * The placeholder to display before the image finishes loading.
     */
    placeholder?: Maybe<HTMLElement>

    /**
     * Loads the image when offscreen if the application is idle.
     */
    loadWhenIdle?: boolean

    /**
     * Loads the image immediately instead of on intersection.
     */
    loadImmediately?: boolean

    /**
     * Properties for intersection, default: `{ rootMargin: '1000px' }`
     */
    intersectionInit?: IntersectionObserverInit
}

const defaultIntersectionInit: IntersectionObserverInit = {
    rootMargin: '1000px',
}

/**
 * An image component, with load intersection handling.
 * Preloads the image when idle.
 *
 * @param props
 * @constructor
 */
export function ImageView(props: ImageProps) {
    const { placeholder, image, loadWhenIdle } = props
    const imageData = asData(image)
    const loaded = data(false)
    if (placeholder) {
        loaded.onData((value) => setVisible(placeholder, !value))
    }
    const imgEl = (
        <img
            crossOrigin="anonymous"
            className="image"
            classList={[loaded.map((value) => (value ? 'loaded' : null))]}
            width={imageData.pick('width')}
            height={imageData.pick('height')}
        />
    ) as HTMLImageElement

    const view = (
        <div
            {...omit(props, ['image', 'placeholder'])}
            className={`imageContainer ${props.className ?? ''}`}
        >
            {imgEl}
            {placeholder}
        </div>
    )

    const intersecting = isIntersectingProvider(
        view,
        props.intersectionInit ?? defaultIntersectionInit
    )
    const imgEvents = new DomEventHost<HTMLElementEventMap>(imgEl)

    function maybeLoadImage() {
        if (props.loadImmediately || intersecting.value || !imageData.value) {
            loadImage().catch(noop)
        } else {
            if (loadWhenIdle && needsLoading) doWhenIdle(loadImage).catch(noop)
        }
    }

    let needsLoading = true

    /**
     * Sets the image src, returning a promise that resolves when the image has finished loading.
     * If src is nullish, removes the src property.
     */
    function loadImage(): Promise<void> {
        if (!needsLoading) return Promise.resolve()
        needsLoading = false
        loaded.value = false
        if (imageData.value) {
            const loadedPromise = nextEventAsPromise(imgEvents, 'load')
                .then(() => {
                    loaded.value = true
                })
                .catch(noop)
            const { src, srcset } = imageData.value
            if (src) imgEl.src = src
            if (srcset) imgEl.srcset = srcset
            return loadedPromise
        } else {
            return Promise.resolve()
        }
    }

    function clearImage() {
        imgEl.removeAttribute('src')
        imgEl.removeAttribute('srcset')
    }

    onConnect(view, () => {
        const { add, dispose } = createDisposer()

        add(
            imageData.onData(() => {
                needsLoading = true
                clearImage()
                maybeLoadImage()
            })
        )
        add(
            intersecting.onData((isIntersecting) => {
                if (isIntersecting) maybeLoadImage()
            })
        )
        return dispose
    })

    return view
}
